import { CSSProperties, FC, memo, useCallback, useContext, useEffect, useState } from 'react'
import { MapCadastralPlotFragment } from '@foros-fe/graphql'
import { Pane, useMap } from 'react-leaflet'
import { buildVectorTileLayerDefaultOptions } from '../utils'
import LayersContext from '../../../contexts/Layers'
import { TaxonomicPlotPopup } from '../Popups/TaxonomicPlotPopup'
import { VectorGrid, Props as VectorGridProps } from './VectorGrid'
import { api } from '../../../config'
import { FeatureLabel, Props as GeoJSONLabelProps } from './FeatureLabel'
import { useLayerPopupHandler } from '../hooks'

type PaneConfig = {
  name: string
  zoomThreshold: number
  style?: CSSProperties
}

type Props = {
  cadastralPlots: MapCadastralPlotFragment[]
  labelsVisible?: boolean
}

const layerPane: PaneConfig = {
  name: 'taxonomicPlotIntersection',
  zoomThreshold: 12,
  style: { zIndex: 500 },
}
const labelPane: PaneConfig = {
  name: 'taxonomicPlotIntersectionLabel',
  zoomThreshold: 15,
  style: { zIndex: 510 },
}
const layerName = 'taxonomicPlots'
const layerSource = 'cadastral_stands'
const vectorGridProps: Partial<VectorGridProps> = {
  options: {
    pane: layerPane.name,
    vectorTileLayerStyles: {
      [layerSource]: {
        ...Object.values(
          buildVectorTileLayerDefaultOptions(layerName, layerSource).vectorTileLayerStyles
        )[0],
        fillOpacity: 0,
        weight: 1,
      },
    },
    getFeatureId: ({ properties }) => properties.taxonomic_plot_id,
  },
  activeStyles: {
    fillOpacity: 0,
    weight: 4,
  },
}

export const TaxonomicPlotIntersectionLayer: FC<Props> = memo(
  ({ cadastralPlots, labelsVisible }) => {
    const { selected } = useContext(LayersContext)
    const map = useMap()
    const [features, setFeatures] = useState<GeoJSONLabelProps['feature'][]>([])
    const setPaneVisibility = useCallback(() => {
      const zoom = map.getZoom()

      const togglePane = ({ name, zoomThreshold }: PaneConfig) => {
        const pane = map.getPane(name)

        if (pane) {
          const { style } = pane
          const visibility = zoom >= zoomThreshold ? 'visible' : 'hidden'

          if (style.visibility !== visibility) {
            style.visibility = visibility
          }
        }
      }

      togglePane(labelPane)
    }, [map])

    useEffect(() => {
      map.on('zoomend fitBounds', setPaneVisibility)

      return () => {
        map.off('zoomend fitBounds', setPaneVisibility)
      }
    }, [map, setPaneVisibility])

    const selectedIds = cadastralPlots.map(({ id }) => `id[]=${id}`).join('&')
    const active = selected.includes(layerName)

    const [popupProps, handleClick] = useLayerPopupHandler(
      active,
      vectorGridProps.options?.getFeatureId
    )

    return active ? (
      <>
        <Pane name={labelPane.name} style={labelPane.style}>
          {features.map((feature) => (
            <FeatureLabel
              key={feature.properties!.stand_number}
              label={feature.properties!.stand_number}
              feature={feature}
            />
          ))}
        </Pane>
        <Pane name={layerPane.name} style={layerPane.style} />
        <VectorGrid
          {...vectorGridProps}
          url={`${api.host}/vector_tiles/${layerSource}/{z}/{x}/{y}.pbf?${selectedIds}`}
          onFeatureLoad={labelsVisible ? setFeatures : null}
          onClick={handleClick}
        />
        {popupProps ? <TaxonomicPlotPopup {...popupProps} /> : null}
      </>
    ) : null
  }
)
